// Alliance (Bowmaker) runner — a TEAM game where each group is a team.
//
// On every hole the team's score is the sum of its best N net (or gross)
// scores, where N depends on par:
//   • par 3        → best 3 scores count
//   • par 4        → best 2 scores count
//   • par 5+       → single best score counts
// The team's total is the sum across all holes played; lowest total wins.
//
// Pure standings only (no betting payouts yet). Operates over the whole field
// (every group), so the tournament leaderboard can rank teams.

import { Course, Hole, Player, ScoreMap } from "../types";
import { TeamSegmentScores } from "./teamBets";

// WHS-style course handicap — mirrors the helper in the other runners so this
// stays standalone.
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

// Best-N-scores-that-count, by hole par.
function countersForPar(par: number): number {
  if (par <= 3) return 3;
  if (par === 4) return 2;
  return 1;
}

export interface AllianceOptions {
  scoringMode?: "gross" | "net";
}

export interface AllianceGroupInput {
  groupId: string;
  groupName?: string;
  players: Player[];
  scores: ScoreMap;
}

export interface AllianceInput {
  course: Course;
  groups: AllianceGroupInput[];
  options: AllianceOptions;
}

export interface AllianceResult {
  // Sorted ascending — lowest combined team score first.
  teams: TeamSegmentScores[];
}

export function runAlliance(input: AllianceInput): AllianceResult {
  const { course, groups, options } = input;
  const useNet = options.scoringMode !== "gross"; // default net
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];

  const teams: TeamSegmentScores[] = groups.map((g) => {
    const hcpById: Record<string, number> = {};
    for (const p of g.players) {
      const tee = tees.find((t) => t.id === p.tee);
      hcpById[p.id] = courseHandicap(
        p.handicap,
        tee?.slope,
        tee?.rating,
        totalPar,
      );
    }

    let overall = 0;
    let front = 0;
    let back = 0;
    let holesPlayed = 0;
    for (const hole of course.holes) {
      const vals: number[] = [];
      for (const p of g.players) {
        const sc = (g.scores[p.id] || {})[hole.number];
        if (!sc || typeof sc.gross !== "number") continue;
        vals.push(
          useNet ? sc.gross - strokesOnHole(hcpById[p.id], hole) : sc.gross,
        );
      }
      if (vals.length === 0) continue;
      holesPlayed++;
      vals.sort((a, b) => a - b);
      const n = Math.min(countersForPar(hole.par), vals.length);
      let contrib = 0;
      for (let i = 0; i < n; i++) contrib += vals[i];
      overall += contrib;
      if (hole.number <= 9) front += contrib;
      else back += contrib;
    }

    return {
      groupId: g.groupId,
      groupName: g.groupName,
      players: g.players,
      overall,
      front,
      back,
      holesPlayed,
    };
  });

  // Lowest combined score wins, but teams that haven't started (no holes
  // played, overall 0) sort to the bottom rather than falsely leading.
  teams.sort((a, b) => {
    if (a.holesPlayed === 0 && b.holesPlayed === 0) return 0;
    if (a.holesPlayed === 0) return 1;
    if (b.holesPlayed === 0) return -1;
    return a.overall - b.overall;
  });
  return { teams };
}
